// Game date for a ticket (earliest game on it), falling back to when it was placed/uploaded.
export const ticketDate = (t) => {
  const ds = (t.legs || []).map((l) => l.event_date || l.game_date).filter(Boolean).map((d) => new Date(d)).filter((d) => !Number.isNaN(d.getTime()));
  const d = ds.length ? new Date(Math.min(...ds)) : new Date(t.placed_at || t.created_at || Date.now());
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles', weekday: 'short', month: 'short', day: 'numeric' });
};
export const isVoid = (l) => l.status === 'void' || l.status === 'push' || l.performance_status === 'VOID';

// When the ticket was uploaded into the app, e.g. "Uploaded Oct 17, 12:41 PM".
export const uploadedAt = (t) => {
  const d = new Date(t.created_at || '');
  return Number.isNaN(d.getTime()) ? '' : `Uploaded ${d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`;
};

// Ticket header says more legs than were read — never show it as WON until fixed or marked.
export const missingLegs = (t) => Number(t.leg_count || 0) > (t.legs || []).length;

export const legKey = (l, i) => l.id || `leg${i}`;
// Legs whose game is on a different day than most of the ticket → { legId: 'Fri, Oct 23' } (Pacific time).
const dayPT = (d) => new Date(d).toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles', weekday: 'short', month: 'short', day: 'numeric' });
export const offDayLegs = (t) => {
  const days = (t.legs || []).map((l, i) => [legKey(l, i), l.event_date || l.game_date]).filter(([, d]) => d && !Number.isNaN(new Date(d).getTime())).map(([id, d]) => [id, dayPT(d)]);
  const n = {};
  days.forEach(([, d]) => { n[d] = (n[d] || 0) + 1; });
  const main = Object.keys(n).sort((a, b) => n[b] - n[a])[0];
  return Object.fromEntries(days.filter(([, d]) => d !== main));
};

// ---- Sorting (sort bar on the tracker) ----
export const SORTS = [['best', 'Best first'], ['worst', 'Worst first'], ['payout', 'Biggest payout'], ['stake', 'Biggest bet'], ['newest', 'Newest']];
const SORT_KEY = 'ticket-sort';
export const getSort = () => localStorage.getItem(SORT_KEY) || 'best';
export const setSort = (m) => { localStorage.setItem(SORT_KEY, m); window.dispatchEvent(new Event('ticket-sort-changed')); };

const tone = (l) => (l.performance_status === 'LIVE_TIED' ? 'yellow' : l.color_token);
// Legs, best first: green, blinking blue, blue, purple, yellow, not started, orange, missed. "Worst first" flips it.
const BEST_RANK = { green: 0, blue: 2, purple: 3, yellow: 4, orange: 6, red: 7 };
export const legRank = (l, color = tone(l)) => {
  const r = color === 'blue' && l.blink ? 1 : BEST_RANK[color] ?? 5;
  return getSort() === 'worst' ? 7 - r : r;
};
export const sortedLegs = (legs) => legs.map((leg, i) => [leg, i]).sort((a, b) => legRank(a[0]) - legRank(b[0]) || a[1] - b[1]);

// Ticket health: green 1, blue .75, purple .5, yellow .25, orange .1 (average over legs).
const LEG_SCORE = { green: 1, blue: 0.75, purple: 0.5, yellow: 0.25, orange: 0.1, red: 0 };
export const ticketScore = (t) => {
  const legs = t.legs || [];
  return legs.length ? legs.reduce((s, l) => s + (LEG_SCORE[tone(l)] ?? 0.2), 0) / legs.length : 0;
};
const tier = (t) => {
  const st = t.status;
  const legs = t.legs || [];
  if (st === 'won' || (legs.length && legs.every((l) => tone(l) === 'green'))) return 0;
  if (st === 'lost' || legs.some((l) => tone(l) === 'red')) return 2;
  return 1;
};
// Best: won on top → live tickets best to worst → lost at the bottom. Worst: the exact reverse.
export const compareTickets = (a, b, mode = getSort()) => {
  const pay = (t) => Number(t.potential_payout) || 0;
  if (mode === 'payout') return pay(b) - pay(a);
  if (mode === 'stake') return (Number(b.stake) || 0) - (Number(a.stake) || 0) || pay(b) - pay(a);
  if (mode === 'newest') return String(b.created_at || '').localeCompare(String(a.created_at || ''));
  const best = tier(a) - tier(b) || ticketScore(b) - ticketScore(a) || pay(b) - pay(a);
  return mode === 'worst' ? -best : best;
};
export const ticketRank = (t) => -ticketScore(t);

// ---- Morning / midday / night (Pacific start time) ----
export const SLOT_NAMES = { morning: 'Morning · before 12 PM PT', midday: 'Midday · 12–4 PM PT', night: 'Night · 4 PM PT and later' };
const SLOT_ORDER = { morning: 0, midday: 1, night: 2 };
const ptHour = (d) => Number(new Date(d).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', hour: 'numeric', hour12: false })) % 24;
export const slotOf = (d) => {
  if (!d || Number.isNaN(new Date(d).getTime())) return 'night';
  const h = ptHour(d);
  return h < 12 ? 'morning' : h < 16 ? 'midday' : 'night';
};
const legTimes = (t) => (t.legs || []).map((l) => new Date(l.event_date || l.game_date || '').getTime()).filter((x) => !Number.isNaN(x));
// A ticket belongs to the window of its LAST game (that's when it settles); unknown times go last.
export const ticketWindow = (t) => {
  const ts = legTimes(t);
  if (!ts.length) return { key: '0000-00-00-9', label: 'No game time yet', first: Infinity };
  const last = new Date(Math.max(...ts));
  const day = last.toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
  const slot = slotOf(last);
  const label = `${last.toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles', weekday: 'short', month: 'short', day: 'numeric' })} · ${SLOT_NAMES[slot]}`;
  return { key: `${day}-${SLOT_ORDER[slot]}`, label, first: Math.min(...ts) };
};
// Newest day first; within a day morning → midday → night; inside a window by start time (30-min buckets), then the chosen sort.
export const groupByWindow = (tickets, mode = getSort()) => {
  const by = {};
  tickets.forEach((t) => { const w = ticketWindow(t); (by[w.key] ||= { ...w, list: [] }).list.push(t); });
  const bucket = (t) => Math.floor(ticketWindow(t).first / 1800000);
  return Object.values(by)
    .sort((a, b) => b.key.slice(0, 10).localeCompare(a.key.slice(0, 10)) || a.key.localeCompare(b.key))
    .map((g) => ({ ...g, list: g.list.sort((a, b) => (mode === 'newest' ? 0 : bucket(a) - bucket(b)) || compareTickets(a, b, mode)) }));
};

// Blinking legs: blue (75%+ late / CFB up 7–9 late) or green (CFB up 10+ late).
export const blinkClass = (blink, color) => (!blink ? '' : color === 'green' ? ' leg-blink-green' : color === 'blue' ? ' leg-blink' : '');
